import { Airport, FlightData } from '../types'
import { t } from 'i18next'
import arLocale from 'date-fns/locale/ar'
import deLocale from 'date-fns/locale/de'
import esLocale from 'date-fns/locale/es'
import frLocale from 'date-fns/locale/fr'
import huLocale from 'date-fns/locale/hu'
import itLocale from 'date-fns/locale/it'
import jaLocale from 'date-fns/locale/ja'
import koLocale from 'date-fns/locale/ko'
import ptLocale from 'date-fns/locale/pt'
import ruLocale from 'date-fns/locale/ru'
import zhCNLocale from 'date-fns/locale/zh-CN'
import zhTWLocale from 'date-fns/locale/zh-TW'
import { differenceInMinutes } from '../utils'

// We define the list of locales here so that we can reduce bundle size by tree-shaking
export function getLocale (language: string): Locale {
  switch (language) {
    case 'ar':
      return arLocale
    case 'de':
      return deLocale
    case 'es':
      return esLocale
    case 'fr':
      return frLocale
    case 'hu':
      return huLocale
    case 'it':
      return itLocale
    case 'ja':
      return jaLocale
    case 'ko':
      return koLocale
    case 'pt':
      return ptLocale
    case 'ru':
      return ruLocale
    case 'zh-CN':
      return zhCNLocale
    case 'zh-TW':
      return zhTWLocale
    default:
      return null
  }
}

export function getCurrentFlight (flights: FlightData[]): FlightData {
  if (flights.length === 1) {
    return flights[0]
  }
  let firstScheduled
  for (const flight of flights) {
    // if the flight is cancelled, then it will never be the current flight
    if (flight.status.includes('Cancelled')) {
      continue
    }
    // if the plane is moving on that flight, then this is definitively the current one
    if (flight.status.includes('OutGate') || flight.status.includes('InAir') || flight.status.includes('Landed')) {
      return flight
    }
    // if plane is not moving, then we take the first flight that is in a scheduled-like status
    if ((flight.status.includes('Scheduled') || flight.status.includes('Delayed') || flight.status.includes('Proposed')) && firstScheduled === undefined) {
      firstScheduled = flight
    }
  }
  // if all the logic above did not find a match, then take the last one
  return firstScheduled || flights[flights.length - 1]
}

export function getStatusLabel (flight: FlightData, additional: boolean = false): string {
  const statuses = flight.status
  let status
  if (statuses.includes('InGate')) {
    status = t('TRACKER.STATUS_LABEL.ARRIVED')
  } else if (statuses.includes('OutGate')) {
    status = t('TRACKER.STATUS_LABEL.DEPARTED')
  } else if (statuses.includes('InAir')) {
    status = t('TRACKER.STATUS_LABEL.IN_AIR')
  } else if (statuses.includes('NoTakeoffInfo')) {
    status = t('TRACKER.STATUS_LABEL.TAKEOFF')
  } else if (statuses.includes('Unknown')) {
    status = t('TRACKER.STATUS_LABEL.RECENT')
  } else if (statuses.includes('Scheduled')) {
    status = t('TRACKER.STATUS_LABEL.SCHEDULED')
  } else if (statuses.includes('Cancelled')) {
    status = t('TRACKER.STATUS_LABEL.CANCELLED')
  } else if (statuses.includes('Delayed')) {
    status = t('TRACKER.STATUS_LABEL.DELAYED')
  } else if (statuses.includes('Landed')) {
    status = t('TRACKER.STATUS_LABEL.LANDED')
  } else {
    status = statuses[0]
  }

  if (additional) {
    if (statuses.includes('Diverted')) {
      status += ` - ${t('TRACKER.STATUS_LABEL.DIVERTED')}`
    }
    if (statuses.includes('Recovery')) {
      status += ` - ${t('TRACKER.STATUS_LABEL.RECOVERED')}`
    }
  }
  return status
}

export function translateStatus (flight: FlightData): string {
  if (flight.status.includes('Scheduled') || flight.status.includes('Proposed') || flight.status.includes('Delayed')) {
    return createScheduledStatus(flight)
  } else if (flight.status.includes('OutGate')) {
    return createOutGateStatus(flight)
  } else if (flight.status.includes('InGate')) {
    return createArrivedStatus(flight)
  } else if (flight.status.includes('InAir')) {
    return createInAirStatus(flight)
  } else if (flight.status.includes('NoTakeoffInfo')) {
    return t('TRACKER.FLIGHT_PART.HEADER_NO_TAKEOFF_INFORMATION')
  } else if (flight.status.includes('Unknown')) {
    return t('TRACKER.FLIGHT_PART.HEADER_NO_RECENT_INFORMATION')
  } else if (flight.status.includes('Cancelled')) {
    return t('TRACKER.STATUS_LABEL.CANCELLED')
  } else if (flight.status.includes('Landed')) {
    return t('TRACKER.STATUS_LABEL.LANDED')
  } else {
    return flight.status[0]
  }
}

function createScheduledStatus (flight: FlightData): string {
  const diff = diffTime(flight.departureAirport)
  if (diff > 0) {
    return t('TRACKER.FLIGHT_PART.HEADER_ESTIMATED_DEPARTURE_EARLY', {
      minutesNumber: diff
    })
  } else if (diff < 0) {
    return t('TRACKER.FLIGHT_PART.HEADER_ESTIMATED_DEPARTURE_LATE', {
      minutesNumber: Math.abs(diff)
    })
  } else {
    return null
  }
}

function createOutGateStatus (flight: FlightData): string {
  const diff = diffTime(flight.departureAirport)
  if (diff === null) {
    return t('TRACKER.FLIGHT_PART.HEADER_DEPARTED_GATE')
  } else if (diff === 0) {
    return t('TRACKER.FLIGHT_PART.HEADER_DEPARTED_GATE_ON_TIME')
  } else if (diff > 0) {
    return t('TRACKER.FLIGHT_PART.HEADER_DEPARTED_GATE_EARLY', {
      minutesNumber: diff
    })
  } else {
    return t('TRACKER.FLIGHT_PART.HEADER_DEPARTED_GATE_LATE', {
      minutesNumber: Math.abs(diff)
    })
  }
}

function createInAirStatus (flight: FlightData): string {
  const diff = diffTime(flight.arrivalAirport)
  if (diff === null) {
    return null
  }
  if (diff === 0) {
    return t('TRACKER.FLIGHT_PART.HEADER_ON_TIME')
  } else if (diff > 0) {
    return t('TRACKER.FLIGHT_PART.HEADER_ESTIMATED_ARRIVAL_EARLY', {
      minutesNumber: diff
    })
  } else {
    return t('TRACKER.FLIGHT_PART.HEADER_ESTIMATED_ARRIVAL_LATE', {
      minutesNumber: Math.abs(diff)
    })
  }
}

function createArrivedStatus (flight: FlightData): string {
  const diff = diffTime(flight.arrivalAirport)
  if (diff === null) {
    return t('TRACKER.FLIGHT_PART.HEADER_ARRIVED_AT_GATE')
  }
  if (diff === 0) {
    return t('TRACKER.FLIGHT_PART.HEADER_ARRIVED_AT_GATE')
  } else if (diff > 0) {
    return t('TRACKER.FLIGHT_PART.HEADER_ARRIVED_AT_GATE_EARLY', {
      minutesNumber: diff
    })
  } else {
    return t('TRACKER.FLIGHT_PART.HEADER_ARRIVED_AT_GATE_LATE', {
      minutesNumber: Math.abs(diff)
    })
  }
}

export function diffTime (airport: Airport): number {
  const scheduled = airport.scheduled
  const actual = airport.actual || airport.estimated
  if (scheduled && actual) {
    return differenceInMinutes(scheduled.getTime(), actual.getTime())
  } else {
    return null
  }
}
